import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';

import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';

import { BasketDiscountResponse } from '@app/api/models';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';
import { cancellationMockDate } from '@shared/constants/cancellation-mock-date';
import { dateToParam } from '@shared/helpers/date-to-param.helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { DialogService } from '@shared/services/dialog.service';
import {
  basketDiscountsGapValidator,
  basketDiscountsOverlapValidator,
} from '@shared/validators/basket-discounts.validator';

import {
  BasketDiscountsActions,
  BasketDiscountsSelectors,
} from './store/basket-discounts.slice';

@Component({
  selector: 'app-basket-discounts',
  templateUrl: './basket-discounts.component.html',
  styleUrls: ['./basket-discounts.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: BasketDiscountsComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: BasketDiscountsComponent,
      multi: true,
    },
  ],
})
export class BasketDiscountsComponent implements OnInit, OnDestroy {
  formGroup: FormGroup = new FormGroup({
    startDate: new FormControl(new Date()),
    endDate: new FormControl(),
    basketDiscounts: new FormArray(
      [],
      [basketDiscountsOverlapValidator(), basketDiscountsGapValidator()]
    ),
  });

  endDateMinimum = new Date();

  subs = new SubSink();

  get formArray(): FormArray {
    return this.formGroup?.get('basketDiscounts') as FormArray;
  }

  private onTouchedCallback = () => {};
  private onChangeCallback = (_: any) => {};

  maxValueErrorStateMatcher = new MaxValueErrorStateMatcher();
  defaultErrorStateMatcher = new ErrorStateMatcher();

  discountModes = ['AMOUNT', 'PERCENT'];

  selectedSiteId!: string;

  constructor(
    private dialogService: DialogService,
    public datePicker: MatDatepickerIntl,
    private store: Store
  ) {
    datePicker.closeCalendarLabel = 'Naptár bezárása';
  }

  ngOnInit(): void {
    this.store.dispatch(BasketDiscountsActions.loadDiscounts.trigger());

    this.subs.add(
      this.store
        .select(AuthenticationSelectors.selectUserInfo)
        .subscribe((userInfo) => {
          if (
            this.selectedSiteId !==
            userInfo.attributes?.['custom:selectedSiteId']
          ) {
            this.selectedSiteId =
              userInfo.attributes?.['custom:selectedSiteId'];
            this.store.dispatch(BasketDiscountsActions.loadDiscounts.trigger());
          }
        })
    );

    this.subs.add(
      this.store
        .select(BasketDiscountsSelectors.selectSortedDiscounts)
        .subscribe((discounts) => this.writeValue(discounts))
    );
  }

  validateMinimumOrderValue() {
    if (this.formArray.length > 0) {
      this.formArray.setValidators([
        basketDiscountsOverlapValidator(),
        basketDiscountsGapValidator(),
      ]);
      this.formArray.updateValueAndValidity();
    }
  }

  writeValue(value: BasketDiscountResponse[]) {
    this.formArray.clear();
    this.formGroup.patchValue({
      startDate: value[0]?.startDate,
      endDate:
        value[0]?.endDate === cancellationMockDate ? null : value[0]?.endDate,
    });
    value?.forEach((data: BasketDiscountResponse, index: number) => {
      this.formArray.push(BasketDiscountsComponent.addRow(data));
    });

    this.validateMinimumOrderValue();

    this.formGroup.valueChanges.subscribe((res) => {
      this.onChangeCallback(res.basketDiscounts);
    });
  }

  get isNewRowEnabled() {
    return (
      (this.formArray.at(this.formArray.length - 1) as FormGroup)?.controls[
        'siteMaxOrderValue'
      ]?.value || this.formArray.length === 0
    );
  }

  public onBlur(): void {
    this.onTouchedCallback();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  validate({ value }: FormControl) {
    return !this.formArray || this.formArray.valid
      ? null
      : this.formArray.errors;
  }

  onStartDateChange(event: string) {
    if (new Date(event).getTime() - new Date().getTime() > 0) {
      this.endDateMinimum = new Date(event);
    } else {
      this.endDateMinimum = new Date();
    }
  }

  private static addRow(
    value?: BasketDiscountResponse,
    lastRow?: BasketDiscountResponse
  ): FormGroup {
    return new FormGroup({
      id: new FormControl(value?.id),
      siteMinOrderValue: new FormControl(
        value?.siteMinOrderValue ??
          (lastRow?.siteMaxOrderValue ? lastRow?.siteMaxOrderValue + 1 : 0)
      ),
      siteMaxOrderValue: new FormControl(
        value?.siteMaxOrderValue === 99999999 ? null : value?.siteMaxOrderValue,
        value?.siteMinOrderValue
          ? Validators.min((value?.siteMinOrderValue ?? 0) + 0.0001)
          : lastRow?.siteMaxOrderValue
          ? Validators.min(lastRow?.siteMaxOrderValue + 1.001)
          : Validators.min(0.001)
      ),
      discountValue: new FormControl(value?.discountValue, Validators.required),
      discountMode: new FormControl(value?.discountMode, Validators.required),
    });
  }

  addNewRow() {
    this.formArray!.push(
      BasketDiscountsComponent.addRow(
        undefined,
        this.formArray.at(this.formArray.length - 1)?.value
      )
    );
  }

  onMinimumValueChange(event: Event, rowIndex: number) {
    let value = +(event.target as HTMLInputElement).value;
    if (rowIndex > 0) {
      (this.formArray.at(rowIndex - 1) as FormGroup).controls[
        'siteMaxOrderValue'
      ].setValue(value - 1);
    }
    (this.formArray.at(rowIndex) as FormGroup).controls[
      'siteMaxOrderValue'
    ].setValidators(Validators.min(value + 0.0001));
    (this.formArray.at(rowIndex) as FormGroup).controls[
      'siteMaxOrderValue'
    ].updateValueAndValidity();
  }

  onMaximumValueChange(event: Event, rowIndex: number) {
    let value = +(event.target as HTMLInputElement).value;
    if (rowIndex < this.formArray.length - 1)
      (this.formArray.at(rowIndex + 1) as FormGroup).controls[
        'siteMinOrderValue'
      ].setValue(value + 1);
  }

  deleteRow(index: number) {
    this.dialogService.confirm(
      'DELIVERY_MODE.EDIT.PRICING_RANGES_TABLE.DELETE_CONFIRM_TITLE',
      'DELIVERY_MODE.EDIT.PRICING_RANGES_TABLE.DELETE_CONFIRM_MESSAGE',
      () => {
        const id = this.formArray.at(index).get('id')?.value;
        if (id) {
          this.store.dispatch(
            BasketDiscountsActions.deleteDiscounts.trigger({ request: [id] })
          );
        }
        this.formArray!.removeAt(index);
      }
    );
  }

  discountModeChanged(idx: number) {
    if (this.formArray.at(idx).get('discountMode')?.value === 'PERCENT') {
      this.formArray
        .at(idx)
        .get('discountValue')
        ?.addValidators(Validators.max(99));
    } else {
      this.formArray
        .at(idx)
        .get('discountValue')
        ?.removeValidators(Validators.max(99));
    }
    this.formArray
      .at(idx)
      .get('discountValue')
      ?.setValidators(
        this.formArray.at(idx).get('discountMode')?.value === 'PERCENT'
          ? [Validators.max(99)]
          : []
      );
    this.formArray.at(idx).get('discountValue')?.updateValueAndValidity();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      this.formGroup.controls['endDate'].setValue('');
    }
  }

  save() {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid && this.formArray.valid) {
      const formValue = trimInput(this.formGroup.value);
      const startDate = formValue.startDate
        ? dateToParam(formValue.startDate)
        : dateToParam(new Date());
      const endDate = formValue.endDate
        ? dateToParam(formValue.endDate)
        : dateToParam(new Date(cancellationMockDate));

      const requestBody = formValue.basketDiscounts.map(
        (item: BasketDiscountResponse) => ({
          ...item,
          startDate: startDate,
          endDate: endDate,
          type: 'BSK',
        })
      );

      this.store.dispatch(
        BasketDiscountsActions.saveDiscounts.trigger({
          request: { discounts: requestBody, fromSiteId: this.selectedSiteId },
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

export class MaxValueErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!control?.invalid;
  }
}
